import React, { useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, Btn } from "./UI";
import { toast } from "sonner";
import { PartyPopper, Loader2, Download, RefreshCcw, CalendarClock, Pencil } from "lucide-react";

export const WeeklyWinsCard = () => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const loadPreview = async (refresh = false) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/political-os/weekly-wins/preview${refresh ? "?refresh=true" : ""}`);
      setPreview(data);
    } catch (e) { toast.error(errMsg(e, "Could not build the recap preview")); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.put("/political-os/weekly-wins/preview", { recap: draft.trim() });
      setPreview(p => ({ ...p, recap: data.recap, edited: true, quiet: false }));
      setEditing(false);
      toast.success("Saved — this exact wording goes out Friday");
    } catch (e) { toast.error(errMsg(e, "Could not save your edits")); }
    finally { setSavingEdit(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/political-os/weekly-wins/share-image", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "weekly-wins.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Share graphic downloaded — post it anywhere");
    } catch (e) { toast.error(errMsg(e, "Could not build the share graphic")); }
    finally { setDownloading(false); }
  };

  return (
    <Card data-testid="weekly-wins-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <PartyPopper className="w-5 h-5 text-emerald-300" />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Weekly Wins</div>
            <h3 className="font-display text-lg font-bold">Friday recap — preview before it sends</h3>
          </div>
        </div>
        {preview && !preview.quiet && !editing && (
          <div className="flex items-center gap-3">
            <button onClick={() => { setDraft(preview.recap || ""); setEditing(true); }} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-300 transition-colors" data-testid="wins-edit-btn">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={() => loadPreview(true)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-300 transition-colors disabled:opacity-50" data-testid="wins-refresh-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />} Rewrite
            </button>
          </div>
        )}
      </div>
      {!preview ? (
        <Btn variant="secondary" onClick={() => loadPreview()} disabled={busy} data-testid="wins-preview-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />}
          {busy ? "Writing your recap…" : "Preview this week's recap"}
        </Btn>
      ) : preview.quiet ? (
        <div className="text-sm text-slate-500" data-testid="wins-quiet">
          Quiet week so far — nothing to recap yet. As verification, finance research and race trends move, they'll show up here.
        </div>
      ) : (
        <div className="space-y-3">
          {editing ? (
            <div className="space-y-2">
              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={8} maxLength={2000}
                data-testid="wins-edit-textarea"
                className="w-full bg-slate-900/70 border border-white/10 rounded-md px-4 py-3 text-sm text-slate-200 leading-relaxed outline-none focus:border-emerald-400/50 font-sans" />
              <div className="flex items-center gap-3">
                <Btn variant="secondary" onClick={saveEdit} disabled={savingEdit || !draft.trim()} data-testid="wins-edit-save-btn">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save wording
                </Btn>
                <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-white" data-testid="wins-edit-cancel-btn">Cancel</button>
              </div>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 leading-relaxed rounded-md border border-white/10 bg-slate-900/50 px-4 py-3" data-testid="wins-recap-text">{preview.recap}</pre>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Btn variant="secondary" onClick={download} disabled={downloading} data-testid="wins-share-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download share graphic
            </Btn>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="wins-sends-on">
              <CalendarClock className="w-3.5 h-3.5" />
              {preview.already_sent ? "Already emailed to your team this week"
                : `${preview.edited ? "Your edited wording emails" : "Emails to your team"} ${preview.sends_on}`}
            </span>
            {preview.edited && !preview.already_sent && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200 border border-emerald-400/25" data-testid="wins-edited-badge">Edited</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
